import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Search, ChevronLeft, ChevronRight, X, DollarSign, FileText, RefreshCw, User
} from 'lucide-react';

function getAgingBadge(days) {
  if (days == null) return 'badge-gray';
  if (days <= 0) return 'badge-success';
  if (days <= 30) return 'badge-warning';
  if (days <= 60) return 'badge-warning';
  if (days <= 90) return 'badge-danger';
  return 'badge-danger';
}

export default function SalesReceivablesPage() {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', customer_id: '', status: '' });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchReceivables();
    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchReceivables();
  }, [pagination.page]);

  const fetchReceivables = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/accounts-receivable', { params });
      if (res.data.success) {
        const data = res.data.data;
        setReceivables(data?.receivables || data?.data || data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || data?.pagination?.total || 0,
          totalPages: data?.totalPages || data?.pagination?.totalPages || Math.ceil((data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers', { params: { limit: 100 } });
      if (res.data.success) setCustomers(res.data.data?.customers || res.data.data || []);
    } catch { /* non-critical */ }
  };

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchReceivables();
  };

  const resetFilters = () => {
    setFilters({ search: '', customer_id: '', status: '' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleReceive = async (id) => {
    try {
      const res = await api.post(`/accounts-receivable/${id}/receive`, {
        payment_date: new Date().toISOString().split('T')[0],
        amount: 0, // full payment
        payment_method: 'cash',
      });
      if (res.data.success) {
        toast.success('Pembayaran berhasil diterima');
        fetchReceivables();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Piutang Penjualan</h1>
          <p className="page-subtitle">Daftar invoice penjualan yang belum dibayar</p>
        </div>
        <button onClick={fetchReceivables} className="btn btn-outline btn-sm">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Perbarui
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="label">Cari</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className="input pl-9"
                  placeholder="Cari invoice..."
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                />
              </div>
            </div>
            <div>
              <label className="label">Pelanggan</label>
              <select
                className="input"
                value={filters.customer_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, customer_id: e.target.value }))}
              >
                <option value="">Semua Pelanggan</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Semua Status</option>
                <option value="unpaid">Belum Dibayar</option>
                <option value="partial">Sebagian</option>
                <option value="paid">Lunas</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button onClick={handleFilter} className="btn btn-primary btn-sm">
                <Search className="h-4 w-4 mr-1.5" /> Cari
              </button>
              <button onClick={resetFilters} className="btn btn-ghost btn-sm">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Pelanggan</th>
                  <th>No. Invoice</th>
                  <th>Tgl. Invoice</th>
                  <th>Jatuh Tempo</th>
                  <th className="text-right">Nilai Awal</th>
                  <th className="text-right">Sisa</th>
                  <th>Aging</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j}><div className="skeleton h-4" /></td>
                      ))}
                    </tr>
                  ))
                ) : receivables.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-gray-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Tidak ada piutang yang belum dibayar</p>
                    </td>
                  </tr>
                ) : (
                  receivables.map((receivable) => (
                    <tr key={receivable.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-200">
                            {receivable.customer?.name || receivable.customer_name || '-'}
                          </span>
                        </div>
                      </td>
                      <td><span className="text-sm font-medium text-gray-900 dark:text-white">{receivable.invoice_number || receivable.invoice_no || '-'}</span></td>
                      <td><span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(receivable.invoice_date || receivable.date)}</span></td>
                      <td><span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(receivable.due_date)}</span></td>
                      <td className="text-right"><span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(receivable.original_amount || receivable.amount)}</span></td>
                      <td className="text-right"><span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(receivable.remaining_balance || receivable.remaining)}</span></td>
                      <td>
                        <span className={`badge ${getAgingBadge(receivable.aging_days || receivable.aging)}`}>
                          {receivable.aging_days ?? receivable.aging ?? 0} hari
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass(receivable.status)}`}>{statusLabel(receivable.status)}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleReceive(receivable.id)}
                            className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded"
                            title="Terima Pembayaran"
                          >
                            <DollarSign className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-footer flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Halaman {pagination.page} dari {pagination.totalPages} ({pagination.total} data)
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="btn btn-outline btn-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-outline btn-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
